/**
 * A client's contract as `client-contracts` returns it (API:
 * services/billing/client-contracts/). A hand-kept mirror; keep it in step.
 *
 * `contractValue` is present only for administrators and the finance roles:
 * for everyone else the API takes it off and says so with `valueHidden: true`,
 * so the page shows "Finance only", never a confident blank. The dates with
 * `…Derived: true` were worked out by HQ (from start + term, or the day after
 * an auto-renewing contract ends), not recorded by anyone.
 *
 * Pure (no React, no MUI).
 */
import { daysBetween } from "../../whiteboard/whiteboardTracking";

export interface ContractRecord {
  id: string;
  clientId: string;
  title?: string | null;
  status?: string | null;
  docType?: string | null;
  startDate?: string | null;
  termMonths?: number | null;
  endDate?: string | null;
  renewalDate?: string | null;
  noticeDays?: number | null;
  autoRenew?: boolean | null;
  contractValue?: string | null;
  valuePeriod?: string | null;
  valueHidden?: boolean;
  notes?: string | null;
  fileName?: string | null;
  isCurrent?: boolean;
  sourceSystem?: string | null;
  // Worked out by the API.
  endsOn?: string | null;
  endsOnDerived?: boolean;
  renewsOn?: string | null;
  renewsOnDerived?: boolean;
  noticeBy?: string | null;
  keyDate?: string | null;
}

/** What the contract is called: its title, else its file's name. */
export const contractTitle = (c: Pick<ContractRecord, "title" | "fileName">): string =>
  c.title?.trim() || c.fileName?.trim() || "Untitled contract";

/** The windows the Contracts page offers for "renewing or ending soon". */
export const UPCOMING_WINDOWS = [30, 60, 90, 180] as const;

/** Statuses a renewal can still matter for. */
const LIVE_STATUSES = new Set(["active", "draft"]);

/**
 * Days until the contract's key date (renewal, else end) — negative when it
 * has passed — or null when no date is known.
 */
export const daysUntil = (c: Pick<ContractRecord, "keyDate">, today: string): number | null =>
  c.keyDate ? daysBetween(today, c.keyDate) : null;

/** A live contract whose renewal (or end) falls within `withinDays`, or whose notice day is within it. */
export function isUpcoming(c: ContractRecord, today: string, withinDays: number): boolean {
  if (c.status && !LIVE_STATUSES.has(c.status)) return false;
  const days = daysUntil(c, today);
  const noticeDays = c.noticeBy ? daysBetween(today, c.noticeBy) : null;
  const near = (d: number | null) => d !== null && d >= 0 && d <= withinDays;
  return near(days) || near(noticeDays);
}

/** The contract form, as typed. */
export interface ContractForm {
  clientId: string;
  title: string;
  status: string;
  docType: string;
  startDate: string;
  termMonths: string;
  endDate: string;
  renewalDate: string;
  noticeDays: string;
  autoRenew: "" | "yes" | "no";
  contractValue: string;
  valuePeriod: string;
  notes: string;
}

export const emptyContractForm = (clientId = ""): ContractForm => ({
  clientId,
  title: "",
  status: "active",
  docType: "",
  startDate: "",
  termMonths: "",
  endDate: "",
  renewalDate: "",
  noticeDays: "",
  autoRenew: "",
  contractValue: "",
  valuePeriod: "",
  notes: "",
});

const blankToNull = (v: string) => (v.trim() === "" ? null : v.trim());
const intOrNull = (v: string) => (v.trim() === "" ? null : Number(v.trim()));

/**
 * The API body for a form. The value is sent only when the person may see
 * values (it is not on the form otherwise), so an edit by someone without them
 * never blanks it.
 */
export function contractBody(form: ContractForm, includeValue: boolean): Record<string, unknown> {
  const body: Record<string, unknown> = {
    title: form.title.trim(),
    status: form.status || "active",
    docType: blankToNull(form.docType),
    startDate: blankToNull(form.startDate),
    termMonths: intOrNull(form.termMonths),
    endDate: blankToNull(form.endDate),
    renewalDate: blankToNull(form.renewalDate),
    noticeDays: intOrNull(form.noticeDays),
    autoRenew: form.autoRenew === "" ? null : form.autoRenew === "yes",
    notes: blankToNull(form.notes),
  };
  if (body.docType === null) delete body.docType;
  if (includeValue) {
    body.contractValue = blankToNull(form.contractValue.replace(/[$,\s]/g, ""));
    body.valuePeriod = blankToNull(form.valuePeriod);
  }
  return body;
}

/** The form for an existing contract. */
export const formFromContract = (c: ContractRecord): ContractForm => ({
  clientId: c.clientId,
  title: c.title ?? c.fileName ?? "",
  status: c.status ?? "active",
  docType: c.docType ?? "",
  startDate: c.startDate ?? "",
  termMonths: c.termMonths === null || c.termMonths === undefined ? "" : String(c.termMonths),
  endDate: c.endDate ?? "",
  renewalDate: c.renewalDate ?? "",
  noticeDays: c.noticeDays === null || c.noticeDays === undefined ? "" : String(c.noticeDays),
  autoRenew: c.autoRenew === true ? "yes" : c.autoRenew === false ? "no" : "",
  contractValue: c.contractValue ? c.contractValue.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "") : "",
  valuePeriod: c.valuePeriod ?? "",
  notes: c.notes ?? "",
});

/** Why the form cannot be saved, or null. */
export function contractFormProblem(form: ContractForm): string | null {
  if (!form.clientId) return "Pick the client.";
  if (!form.title.trim()) return "Give the contract a title.";
  if (form.termMonths.trim() && !/^\d+$/.test(form.termMonths.trim())) return "The term is a whole number of months.";
  if (form.noticeDays.trim() && !/^\d+$/.test(form.noticeDays.trim())) return "The notice period is a whole number of days.";
  if (form.contractValue.trim() && !/^\d+(\.\d{1,4})?$/.test(form.contractValue.replace(/[$,\s]/g, ""))) {
    return "The value is an amount with up to four decimal places.";
  }
  if (form.startDate && form.endDate && form.endDate < form.startDate) return "The end date is before the start date.";
  return null;
}
